import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import i18n from "@/plugins/i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { documentsApi } from "../infrastructure/api/documents-api";
import { registryApi } from "../infrastructure/api/registry-api";
import { lettersApi } from "../infrastructure/api/letters-api";
/** True when an axios error is an HTTP 403 (missing OPDOC editor rights). */
function isForbidden(err) {
    return (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "status" in err.response &&
        err.response.status === 403);
}
export const useDocumentsStore = defineStore("documents", () => {
    // ── List state ──
    const documents = ref([]);
    const pagination = reactive({
        page: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
    });
    // All filters + sort are applied server-side by OPDOC — the view syncs the
    // table query state into this object, then calls fetchDocuments().
    const filters = reactive({});
    const listLoading = ref(false);
    const listError = ref(null);
    // ── Single-document state (editor) ──
    const currentDocument = ref(null);
    const editorConfig = ref(null);
    const editorLoading = ref(false);
    const editorError = ref(null);
    // R97: the search box re-fetches as-you-type (debounced) and the tab/filter/sort
    // watchers re-fetch with no debounce at all, so an earlier slow response can land
    // AFTER a later one. Only the latest call may write the list, the pagination, the
    // error or the loading flag — same guard as validateTags below.
    let listCallSeq = 0;
    async function fetchDocuments() {
        const seq = ++listCallSeq;
        // An empty classification list matches nothing, while the endpoint reads an
        // absent `classification` as "every category": answer it here, without a call.
        if (filters.classifications?.length === 0) {
            documents.value = [];
            pagination.page = 1;
            pagination.totalCount = 0;
            pagination.totalPages = 0;
            listError.value = null;
            listLoading.value = false;
            return;
        }
        listLoading.value = true;
        listError.value = null;
        try {
            const params = {
                page: pagination.page,
                pageSize: pagination.pageSize,
                ...filters,
            };
            const result = await documentsApi.getDocuments(params);
            if (seq !== listCallSeq)
                return;
            documents.value = result.items;
            pagination.page = result.page;
            pagination.pageSize = result.pageSize;
            pagination.totalCount = result.totalCount;
            pagination.totalPages = result.totalPages;
        }
        catch {
            if (seq !== listCallSeq)
                return;
            listError.value = i18n.global.t("document.error.loadFailed");
        }
        finally {
            if (seq === listCallSeq) {
                listLoading.value = false;
            }
        }
    }
    /**
     * Loads the row the editor is about to open. Templates and letters live on
     * separate endpoints (a letter is private to its author) but share this
     * state, so the editor view stays one component.
     */
    async function loadDocument(id, source = "template") {
        editorLoading.value = true;
        editorError.value = null;
        try {
            currentDocument.value =
                source === "letter" ? await lettersApi.getLetter(id) : await documentsApi.getDocument(id);
        }
        catch (err) {
            editorError.value = i18n.global.t("document.editor.error.loadFailed");
            throw err;
        }
        finally {
            editorLoading.value = false;
        }
    }
    async function fetchEditorConfig(id, source = "template") {
        editorError.value = null;
        try {
            editorConfig.value =
                source === "letter"
                    ? await lettersApi.getLetterEditorConfig(id)
                    : await documentsApi.getEditorConfig(id);
        }
        catch (err) {
            // The editor session is an editor-capability surface. The FE hides the
            // entry points, but the OPUS per-organization rights can differ from
            // the Keycloak role (two-layer policy) — a residual 403 must read as
            // "no editing rights", not as a technical failure.
            editorError.value = i18n.global.t(isForbidden(err) ? "document.editor.error.forbidden" : "document.editor.error.loadFailed");
            throw err;
        }
    }
    /**
     * Surfaces an editor failure the store did not raise itself — the Office
     * DocServer mount errors the OfficeEditor component reports (script load
     * rejected, DocsAPI absent, constructor throw, DocServer onError). Without
     * this the view keeps rendering an empty editor pane with no explanation.
     */
    function setEditorError(message) {
        editorError.value = message;
    }
    function clearEditor() {
        currentDocument.value = null;
        editorConfig.value = null;
        editorError.value = null;
    }
    // ── Mutation state ──
    // Mutation errors are surfaced exclusively via toasts in the view layer
    // (see useAppToast). The handlers re-throw so callers can `await` and
    // sequence follow-up UI (close-modal etc.) without consulting the store.
    const createLoading = ref(false);
    const updateLoading = ref(false);
    const deleteLoading = ref(false);
    const duplicateLoading = ref(false);
    const archiveLoading = ref(false);
    async function createDocument(file, name, description, templateKind, classification) {
        createLoading.value = true;
        try {
            const created = await documentsApi.createDocument(file, name, description, templateKind);
            // Classification is a PATCH concern upstream (the multipart create only
            // carries name/description/kind) — apply it right after the upload when
            // the user picked one in the modal.
            if (!classification) {
                await fetchDocuments();
                return { document: created, classificationSkipped: false };
            }
            try {
                const classified = await documentsApi.updateDocument(created.id, classification);
                await fetchDocuments();
                return { document: classified, classificationSkipped: false };
            }
            catch (err) {
                // The upload already succeeded and is irreversible from here: rethrowing would report
                // "creation failed" for a document that exists, leaving the modal open on a file the
                // user would re-submit — a second upload that now collides with the unique name and
                // 409s, with the first copy still sitting unclassified in the library. Report the
                // partial outcome instead and let the caller point the user at the metadata modal,
                // which sets exactly this field on an existing row.
                logger.error("[useDocumentsStore] Classification of the created document failed", err);
                await fetchDocuments();
                return { document: created, classificationSkipped: true };
            }
        }
        finally {
            createLoading.value = false;
        }
    }
    async function updateDocument(id, body) {
        updateLoading.value = true;
        try {
            const updated = await documentsApi.updateDocument(id, body);
            // Patch the in-memory list item so the table reflects the change without
            // a full refetch (the upstream order is determined by server-side sort).
            const idx = documents.value.findIndex((d) => d.id === id);
            if (idx >= 0) {
                documents.value[idx] = {
                    ...documents.value[idx],
                    name: updated.name,
                    description: updated.description,
                    contextKey: updated.contextKey,
                    documentTypeKey: updated.documentTypeKey,
                    updatedAt: updated.updatedAt,
                    updatedByName: updated.updatedByName,
                };
            }
            if (currentDocument.value?.id === id) {
                currentDocument.value = updated;
            }
            return updated;
        }
        finally {
            updateLoading.value = false;
        }
    }
    async function duplicateDocument(id, name) {
        duplicateLoading.value = true;
        try {
            const copy = await documentsApi.duplicateDocument(id, name);
            // The copy's position depends on the server-side sort — refetch instead
            // of guessing where to insert it.
            await fetchDocuments();
            return copy;
        }
        finally {
            duplicateLoading.value = false;
        }
    }
    async function archiveDocument(id) {
        archiveLoading.value = true;
        try {
            const archived = await documentsApi.archiveDocument(id);
            // The default library view excludes archived rows — refetch so the row
            // disappears (or stays, when the user filters on Archived) consistently.
            await fetchDocuments();
            if (currentDocument.value?.id === id) {
                currentDocument.value = archived;
            }
            return archived;
        }
        finally {
            archiveLoading.value = false;
        }
    }
    // ── Registry contexts (classification dropdowns + category labels) ──
    const registryContexts = ref([]);
    const registryContextsLoading = ref(false);
    let registryContextsLoaded = false;
    /**
     * Loads the registry contexts once per session (labels only change with a
     * registry redeploy). Silent failure keeps the library usable — the category
     * column then falls back to technical keys and the dropdowns stay empty.
     */
    async function loadRegistryContexts() {
        if (registryContextsLoaded || registryContextsLoading.value)
            return;
        registryContextsLoading.value = true;
        try {
            registryContexts.value = await registryApi.getContexts();
            registryContextsLoaded = true;
        }
        catch (err) {
            logger.error("[useDocumentsStore] Failed to load registry contexts", err);
        }
        finally {
            registryContextsLoading.value = false;
        }
    }
    async function deleteDocument(id) {
        deleteLoading.value = true;
        try {
            await documentsApi.deleteDocument(id);
            // Remove from the in-memory list and decrement the paginated total so
            // the Pager reflects the new size without a full refetch. If the deleted
            // doc was the currently-open one, clear the editor.
            const idx = documents.value.findIndex((d) => d.id === id);
            if (idx >= 0) {
                documents.value.splice(idx, 1);
                if (pagination.totalCount > 0) {
                    pagination.totalCount -= 1;
                    pagination.totalPages = Math.max(1, Math.ceil(pagination.totalCount / pagination.pageSize));
                }
            }
            if (currentDocument.value?.id === id) {
                clearEditor();
            }
        }
        finally {
            deleteLoading.value = false;
        }
    }
    // ── Tag validation + publish state (template authoring) ──
    const validationResult = ref(null);
    const validationLoading = ref(false);
    const validationError = ref(null);
    // Set when the editor reported a content change since the last check —
    // the panel shows a "re-check" hint. Publish itself never trusts the
    // cached result: the backend re-derives on every activate (R89).
    const validationStale = ref(false);
    const activateLoading = ref(false);
    const previewLoading = ref(false);
    // Only the latest in-flight validation call may write the panel state — a
    // slow response for a previous document (the store is a global singleton
    // surviving navigation) or a superseded re-check must not overwrite what
    // the user is currently looking at (R97 principle). clearValidation bumps
    // the sequence so unmount invalidates whatever is still in flight.
    let validationCallSeq = 0;
    async function validateTags(id) {
        const seq = ++validationCallSeq;
        validationLoading.value = true;
        validationError.value = null;
        try {
            const result = await documentsApi.getTagValidation(id);
            if (seq !== validationCallSeq)
                return result;
            validationResult.value = result;
            validationStale.value = false;
            return result;
        }
        catch (err) {
            if (seq !== validationCallSeq)
                return null;
            logger.error("[useDocumentsStore] Tag validation failed", err);
            validationError.value = i18n.global.t("document.validation.loadError");
            return null;
        }
        finally {
            if (seq === validationCallSeq) {
                validationLoading.value = false;
            }
        }
    }
    function markValidationStale() {
        validationStale.value = true;
    }
    /**
     * Publishes (activates) a document. Always POSTs — the 422 refusal is the
     * authoritative gate, never a cached FE flag (R89). On a
     * `Document.Activation.InvalidTags` refusal the invalid-tag list from the
     * ProblemDetails `errorResult` repopulates `validationResult` so the panel
     * re-opens with fresh data; the error is rethrown for the view's toast.
     */
    async function activateDocument(id) {
        activateLoading.value = true;
        // The validation panel belongs to the document on screen: skip the
        // validation-state writes when the user navigated away before the
        // response landed (same staleness class as validateTags).
        const panelStillOwnsDocument = () => currentDocument.value?.id === id;
        try {
            const activated = await documentsApi.activateDocument(id);
            if (currentDocument.value?.id === id) {
                currentDocument.value = activated;
            }
            const idx = documents.value.findIndex((d) => d.id === id);
            if (idx >= 0) {
                documents.value[idx] = { ...documents.value[idx], status: activated.status };
            }
            if (panelStillOwnsDocument()) {
                validationResult.value = {
                    status: "Valid",
                    tagCount: validationResult.value?.tagCount ?? 0,
                    invalidTags: [],
                };
                validationStale.value = false;
            }
            return activated;
        }
        catch (err) {
            if (panelStillOwnsDocument()) {
                const invalidTags = extractInvalidTagsFromError(err);
                if (invalidTags) {
                    validationResult.value = {
                        status: "Invalid",
                        tagCount: validationResult.value?.tagCount ?? invalidTags.length,
                        invalidTags,
                    };
                    validationStale.value = false;
                }
                else {
                    // Refusal without a parsable tag list: force a re-check so the panel
                    // never shows a stale "valid" next to a failed publish.
                    validationStale.value = true;
                }
            }
            throw err;
        }
        finally {
            activateLoading.value = false;
        }
    }
    async function previewWithSampleData(id) {
        previewLoading.value = true;
        try {
            return await documentsApi.previewWithSampleData(id);
        }
        finally {
            previewLoading.value = false;
        }
    }
    function clearValidation() {
        // Invalidate any in-flight validation call so a late response for the
        // previous document cannot repopulate the panel after the reset.
        validationCallSeq++;
        validationResult.value = null;
        validationError.value = null;
        validationStale.value = false;
        validationLoading.value = false;
    }
    return {
        documents,
        pagination,
        filters,
        listLoading,
        listError,
        currentDocument,
        editorConfig,
        editorLoading,
        editorError,
        setEditorError,
        createLoading,
        updateLoading,
        deleteLoading,
        duplicateLoading,
        archiveLoading,
        registryContexts,
        registryContextsLoading,
        validationResult,
        validationLoading,
        validationError,
        validationStale,
        activateLoading,
        previewLoading,
        fetchDocuments,
        loadDocument,
        fetchEditorConfig,
        clearEditor,
        createDocument,
        updateDocument,
        deleteDocument,
        duplicateDocument,
        archiveDocument,
        loadRegistryContexts,
        validateTags,
        markValidationStale,
        activateDocument,
        previewWithSampleData,
        clearValidation,
    };
});
/**
 * Reads `errorResult.invalidTags` out of an OPDOC 422
 * `Document.Activation.InvalidTags` ProblemDetails body; `null` for any other
 * error shape.
 */
function extractInvalidTagsFromError(err) {
    if (err !== null &&
        typeof err === "object" &&
        "response" in err &&
        err.response !== null &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data !== null &&
        typeof err.response.data === "object" &&
        "errorResult" in err.response.data) {
        const errorResult = err.response.data.errorResult;
        if (errorResult !== null &&
            typeof errorResult === "object" &&
            "invalidTags" in errorResult &&
            Array.isArray(errorResult.invalidTags)) {
            return (errorResult.invalidTags)
                .filter((tag) => typeof tag === "string")
                .map((path) => ({ raw: `{d.${path}}`, path }));
        }
    }
    return null;
}
